import React, { useState, useEffect } from 'react';
import { Package, Plus, Trash2, AlertTriangle } from 'lucide-react';
import { Drawer } from '../common/Drawer';
import { Button } from '../common/Button';
import { CustomSelect } from '../common/CustomSelect';
import { useDatabase } from '../../db/DatabaseProvider';
import { useBranches, useBaseCurrency } from '../../db/hooks/useLookupData';
import { useToast } from '../common/ToastProvider';
import { generateId, now } from '../../lib/uuid';
import type { PackageDocType, PackageItemType, ServiceDocType } from '../../db/schemas';

interface PackageFormDrawerProps {
  isOpen: boolean;
  onClose: () => void;
  combo?: PackageDocType | null;
  services: ServiceDocType[];
}

export function PackageFormDrawer({ isOpen, onClose, combo, services }: PackageFormDrawerProps) {
  const db = useDatabase();
  const { branches } = useBranches();
  const { currencySymbol } = useBaseCurrency();
  const toast = useToast();

  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [currencyPrice, setCurrencyPrice] = useState<number | ''>('');
  const [branchId, setBranchId] = useState('');
  const [items, setItems] = useState<PackageItemType[]>([]);

  useEffect(() => {
    if (isOpen) {
      if (combo) {
        setName(combo.name);
        setDescription(combo.description || '');
        setCurrencyPrice(combo.currency_price);
        setBranchId(combo.branch_id || '');
        setItems(combo.items || []);
      } else {
        setName('');
        setDescription('');
        setCurrencyPrice('');
        setBranchId('');
        const defaultServId = services[0]?.id || '';
        setItems(defaultServId ? [{ id: generateId('pitem'), service_id: defaultServId, session_count: 3 }] : []);
      }
    }
  }, [isOpen, combo, services]);

  const addItem = () => {
    if (!services || services.length === 0) {
      toast.warning('Primero debes registrar servicios en el catálogo.', 'Sin Servicios');
      return;
    }
    const defaultServId = services[0].id;
    setItems([...items, { id: generateId('pitem'), service_id: defaultServId, session_count: 1 }]);
  };

  const removeItem = (index: number) => {
    setItems(items.filter((_, i) => i !== index));
  };

  const updateItem = (index: number, field: keyof PackageItemType, val: any) => {
    const next = [...items];
    let cleanedVal = val;
    if (field === 'session_count') {
      cleanedVal = Math.max(1, parseInt(val) || 1);
    }
    next[index] = { ...next[index], [field]: cleanedVal };
    setItems(next);
  };

  const regularPriceSum = items.reduce((acc, item) => {
    const s = services.find((serv) => serv.id === item.service_id);
    return acc + (s ? s.currency_price * item.session_count : 0);
  }, 0);

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!db || !name.trim()) {
      toast.error('El nombre del paquete/combo es obligatorio.', 'Campo Requerido');
      return;
    }

    if (!services || services.length === 0) {
      toast.error('Debes registrar al menos un servicio en el catálogo antes de crear un paquete.', 'Sin Servicios Disponibles');
      return;
    }

    if (items.length === 0) {
      toast.error('El paquete debe incluir al menos un servicio.', 'Sin Servicios');
      return;
    }

    const consolidatedMap = new Map<string, number>();
    items.forEach((it) => {
      if (it.service_id) {
        const qty = Math.max(1, Number(it.session_count) || 1);
        consolidatedMap.set(it.service_id, (consolidatedMap.get(it.service_id) || 0) + qty);
      }
    });

    const sanitizedItems: PackageItemType[] = Array.from(consolidatedMap.entries()).map(([sId, qty]) => ({
      id: generateId('pitem'),
      service_id: sId,
      session_count: qty,
    }));

    if (sanitizedItems.length === 0) {
      toast.error('Debe seleccionar servicios válidos para el paquete.', 'Servicio Requerido');
      return;
    }

    const invalidItem = sanitizedItems.find((it) => !services.some((s) => s.id === it.service_id));
    if (invalidItem) {
      toast.error('Uno de los servicios seleccionados no es válido o ya no existe.', 'Servicio Inválido');
      return;
    }

    const price = typeof currencyPrice === 'number' ? currencyPrice : regularPriceSum;
    if (price < 0) {
      toast.error('El precio del paquete no puede ser negativo.', 'Precio Inválido');
      return;
    }

    const timestamp = now();

    try {
      if (combo) {
        const doc = await db.packages.findOne({ selector: { id: combo.id } }).exec();
        if (doc) {
          await doc.patch({
            name: name.trim(),
            description: description.trim() || null,
            currency_price: price,
            branch_id: branchId || null,
            is_active: true,
            items: sanitizedItems,
            updated_at: timestamp,
          });
        }
      } else {
        await db.packages.insert({
          id: generateId('paq'),
          name: name.trim(),
          description: description.trim() || null,
          currency_price: price,
          branch_id: branchId || null,
          is_active: true,
          items: sanitizedItems,
          created_at: timestamp,
          updated_at: timestamp,
          is_deleted: false,
        });
      }
      toast.success(combo ? 'Combo/paquete actualizado con éxito' : 'Combo/paquete guardado exitosamente');
      onClose();
    } catch (err: any) {
      toast.error('Error al guardar paquete/combo: ' + err.message);
    }
  };

  return (
    <Drawer
      isOpen={isOpen}
      onClose={onClose}
      title={combo ? 'Editar Combo / Paquete' : 'Nuevo Combo / Paquete'}
      subtitle="Selecciona los servicios incluidos y define el precio especial del combo"
      icon={Package}
      maxWidth="lg"
      footer={
        <div className="flex justify-end gap-2 w-full">
          <Button variant="ghost" size="sm" onClick={onClose}>
            Cancelar
          </Button>
          <Button
            variant="primary"
            size="sm"
            type="submit"
            form="combo-form"
            onClick={handleSave}
            disabled={services.length === 0}
          >
            {combo ? 'Actualizar Combo' : 'Guardar Combo / Paquete'}
          </Button>
        </div>
      }
    >
      <form id="combo-form" onSubmit={handleSave} className="space-y-4">
        {services.length === 0 && (
          <div className="p-3.5 bg-amber-500/10 border border-amber-500/30 rounded-2xl flex items-center gap-3 text-xs text-amber-600 font-semibold animate-fade-in shadow-xs">
            <AlertTriangle className="w-4 h-4 shrink-0 text-amber-500" />
            <span>No hay servicios registrados en el catálogo. Crea primero al menos un servicio antes de configurar paquetes o combos.</span>
          </div>
        )}
        <div>
          <label className="block text-xs font-semibold text-text-secondary mb-1">
            Nombre del Paquete / Combo *
          </label>
          <input
            type="text"
            required
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="Ej: Combo Axilas 5x + Bozo"
            className="w-full rounded-xl border border-border bg-surface px-3.5 py-2.5 text-sm text-text-primary focus:border-brand-sky focus:ring-2 focus:ring-brand-sky/20 transition-all shadow-xs"
          />
        </div>

        <div>
          <label className="block text-xs font-semibold text-text-secondary mb-1">
            Descripción (Opcional)
          </label>
          <textarea
            rows={2}
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder="Promoción especial para atenciones consecutivas..."
            className="w-full rounded-xl border border-border bg-surface px-3.5 py-2.5 text-sm text-text-primary focus:border-brand-sky focus:ring-2 focus:ring-brand-sky/20 transition-all shadow-xs resize-none"
          />
        </div>

        <div>
          <label className="block text-xs font-semibold text-text-secondary mb-1">
            Sucursal Asignada
          </label>
          <CustomSelect
            value={branchId}
            onChange={setBranchId}
            options={[
              { value: '', label: 'Todas las Sucursales (Global)' },
              ...branches.map((s) => ({ value: s.id, label: s.name })),
            ]}
          />
        </div>

        {/* Items inclusion list */}
        <div className="space-y-3 pt-2">
          <div className="flex justify-between items-center">
            <label className="block text-xs font-bold text-text-primary">
              Servicios Incluidos (Atenciones) *
            </label>
            <Button type="button" variant="ghost" size="xs" icon={Plus} onClick={addItem}>
              Agregar Servicio
            </Button>
          </div>

          {items.map((item, idx) => (
            <div key={idx} className="flex items-center gap-2 bg-surface-secondary p-3 rounded-xl border border-border">
              <div className="flex-1">
                <CustomSelect
                  value={item.service_id}
                  onChange={(val) => updateItem(idx, 'service_id', val)}
                  options={services.map((s) => ({
                    value: s.id,
                    label: `${s.concept} (${currencySymbol}${s.currency_price.toFixed(2)})`,
                  }))}
                />
              </div>

              <div className="flex items-center gap-1 shrink-0">
                <span className="text-[11px] font-semibold text-text-secondary">Cant:</span>
                <input
                  type="number"
                  inputMode="decimal"
                  min="1"
                  value={item.session_count}
                  onChange={(e) => updateItem(idx, 'session_count', parseInt(e.target.value) || 1)}
                  className="w-16 px-2.5 py-2 rounded-xl border border-border bg-surface text-xs font-bold text-center text-text-primary focus:border-brand-sky shadow-xs"
                />
              </div>

              <Button
                type="button"
                variant="danger"
                size="xs"
                icon={Trash2}
                onClick={() => removeItem(idx)}
                title="Remover servicio"
                aria-label="Remover servicio"
              />
            </div>
          ))}
        </div>

        {/* Price calculations */}
        <div className="bg-brand-orange/5 border border-brand-orange/20 p-4 rounded-xl flex justify-between items-center mt-4">
          <div>
            <p className="text-xs text-text-secondary mb-1">Precio regular: {currencySymbol}{regularPriceSum.toFixed(2)}</p>
            <label className="block text-sm font-bold text-brand-orange">
              Precio del Paquete *
            </label>
          </div>
          <div className="w-1/3">
            <input
              type="number"
              inputMode="decimal"
              step="0.01"
              required
              min="0"
              value={currencyPrice}
              onChange={(e) => setCurrencyPrice(e.target.value === '' ? '' : parseFloat(e.target.value))}
              placeholder={regularPriceSum.toFixed(2)}
              className="w-full rounded-xl border border-brand-orange/30 bg-surface px-3 py-2 text-sm font-bold text-brand-orange focus:border-brand-orange focus:ring-2 focus:ring-brand-orange/20 text-right shadow-xs"
            />
          </div>
        </div>
      </form>
    </Drawer>
  );
}
